// src/rms/context/AuthContext.jsx
// ============================================================================
// Authentication
// ============================================================================
// Every RLS policy in the schema keys off auth.uid(). Without a session, a
// Supabase-backed build returns zero rows everywhere and rejects every insert —
// so this is not an optional layer, it is what makes the backend function.
//
// LOCAL BACKEND: there is no auth server, so this reports a synthetic signed-in
// developer. That keeps `npm start` working with no project attached, which is
// the whole point of the local adapter. The synthetic user never reaches a
// database; RLS is the real boundary and it only exists on Supabase.
// ============================================================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isLocalBackend } from "../data";
import { LOCAL_USER_ID, LOCAL_USER_EMAIL } from "../data/localAdapter";
import { supabase, isSupabaseConfigured } from "../config/supabaseClient";

const AuthContext = createContext(null);

/**
 * Stand-in identity for local development. Never touches a real database.
 *
 * The id comes from the adapter rather than being repeated here: the adapter
 * writes the owner's member row with it, and two copies of the same uuid in
 * two files is exactly the kind of thing that drifts and then silently stops
 * matching any row.
 */
const LOCAL_USER = {
  id: LOCAL_USER_ID,
  email: LOCAL_USER_EMAIL,
  isLocal: true,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(isLocalBackend ? LOCAL_USER : null);
  const [loading, setLoading] = useState(!isLocalBackend);
  const [error, setError] = useState(null);

  /**
   * True between following a recovery link and choosing a new password.
   *
   * A recovery link produces a REAL session -- the person is signed in the
   * moment they click it, whether or not they ever set a password. So this is
   * not an access flag; it exists so the app can carry them to /reset from
   * wherever the link happened to land them. Supabase sends recovery links to
   * the Site URL when the redirect target is not in the allow-list, and a
   * silent sign-in on the marketing page is exactly the dead end this flow was
   * reported broken for.
   */
  const [recovery, setRecovery] = useState(false);

  useEffect(() => {
    if (isLocalBackend || !isSupabaseConfigured) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setUser(data?.session?.user || null);
      setLoading(false);
    });

    // Keeps the app in step with token refreshes, sign-out from another tab,
    // and email-confirmation redirects.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      setUser(session?.user || null);
      setLoading(false);
      // Fired once, when detectSessionInUrl consumes a recovery token. There is
      // no other signal: by the time the app renders, a recovery session and an
      // ordinary one are indistinguishable.
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    setError(null);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
    setUser(data.user);
    return { success: true, data: data.user };
  }, []);

  const signUp = useCallback(async (email, password) => {
    setError(null);
    const { data, error: err } = await supabase.auth.signUp({ email, password });
    if (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
    // With email confirmation enabled Supabase returns a user but no session;
    // the caller has to tell the operator to go and confirm.
    return {
      success: true,
      data: data.user,
      needsConfirmation: !data.session,
    };
  }, []);

  /**
   * Send a recovery link.
   *
   * Always reports success. Whether an address has an account is not this
   * screen's to disclose -- a form that answers differently for a known and an
   * unknown address is an account-enumeration oracle, and the caller's copy
   * says "if there is an account" for that reason. Supabase behaves the same
   * way at the API, so the only errors that reach here are transport and rate
   * limits, which the operator does need to see.
   *
   * redirectTo must also be listed under Authentication -> URL Configuration ->
   * Redirect URLs. Supabase silently falls back to the Site URL when it is not,
   * which lands the person on the landing page holding a spent token.
   */
  const requestPasswordReset = useCallback(async (email) => {
    setError(null);
    // The local backend has no auth server, and the Supabase client throws on
    // first property access when it is unconfigured -- so this has to refuse
    // BEFORE touching it, not on the way back. Reached only if the sign-in
    // screen leaves the affordance visible, which it does not.
    if (isLocalBackend || !isSupabaseConfigured) {
      return { success: false, error: "There are no accounts on the local backend, so there is no password to reset." };
    }
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset`,
    });
    if (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
    return { success: true };
  }, []);

  /**
   * Set a new password on the CURRENT session.
   *
   * Works for a recovery session and for an ordinary signed-in one, which is
   * why /reset doubles as the change-password screen. It cannot work without a
   * session at all -- that case is the expired link, and the page says so
   * rather than letting the form fail on submit.
   */
  const updatePassword = useCallback(async (password) => {
    setError(null);
    const { data, error: err } = await supabase.auth.updateUser({ password });
    if (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
    // The link is spent and the password is chosen; nothing left to carry.
    setRecovery(false);
    if (data?.user) setUser(data.user);
    return { success: true, data: data?.user };
  }, []);

  const signOut = useCallback(async () => {
    if (isLocalBackend) return;
    await supabase.auth.signOut();
    setUser(null);
    setRecovery(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      userId: user?.id || null,
      email: user?.email || null,
      loading,
      error,
      isAuthenticated: !!user,
      // True when auth is a real gate rather than a local stand-in. Screens use
      // this to decide whether to show sign-in affordances at all.
      authEnforced: !isLocalBackend && isSupabaseConfigured,
      // Follow a recovery link and you are signed in but have no password. The
      // app uses this to finish the job rather than leaving you there.
      recovery,
      signIn,
      signUp,
      signOut,
      requestPasswordReset,
      updatePassword,
    }),
    [user, loading, error, recovery, signIn, signUp, signOut, requestPasswordReset, updatePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export default AuthContext;
